import { useMemo, useState } from 'react';
import {
  defaultOutreachTimeZone,
  outreachFormRespondUrl,
  outreachProductName,
  unfilledOutreachVariables,
} from '@aegislab/shared';
import { getApiErrorMessage } from '@/shared/lib/getApiErrorMessage';
import { getApiErrorMeta } from '@/shared/lib/getApiErrorMeta';
import { Button } from '@/shared/ui/atoms';
import { useAuthStore } from '@/shared/model';
import { useScenarioDetail } from '@/entities/scenario';
import {
  useOutreachDraft,
  useOutreachPresenceLease,
  useSendOutreachTestMail,
  useStibeeLists,
} from '@/entities/outreach-draft';
import {
  useOutreachBulkPreview,
  useOutreachOutlierSummary,
  useReserveOutreachBulk,
  useSendOutreachBulk,
} from '@/entities/outreach-candidate';
import {
  SendConfirmPanel,
  type ScheduleDraft,
  type SendConfirmStep,
  type SendMode,
  type SharedAddressGroup,
} from './SendConfirmPanel';
import { plainNote } from './SendConfirmPanel.css';
import { SendConfirmReinclude } from './SendConfirmReinclude';

/** 서버가 확정 직전 새 회신·폼 제출을 찾아 요청 전체를 거절한 코드(재포함 명세 §2-4). */
const REINCLUDE_CHANGED = 'OUTREACH_REINCLUDE_CHANGED';

export interface SendConfirmContainerProps {
  scenarioId: string;
  country: string;
  onGoTo: (step: SendConfirmStep) => void;
  /** 발송이 받아들여진 뒤 — 덮개가 이력을 다시 부르고 면을 닫는다. */
  onSent: () => void;
}

const NO_SENDER_REASON = '주소록과 보내는 사람을 작성기에서 골라 주세요.';

/** 고를 수 있는 시간대 — 브라우저가 아는 전체. 기본값이 목록에 없으면 앞에 붙인다. */
function timeZoneOptionsFor(defaultZone: string): string[] {
  const zones =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  return zones.includes(defaultZone) ? zones : [defaultZone, ...zones];
}

/**
 * 발송 확인의 배선 — 초안 · 명단 미리보기 · 이상치 건수를 모아 패널에 넘기고,
 * 누르면 `POST …/outreach-rounds/bulk` 를 부른다(퀘스트 7ca8908c).
 *
 * 초안은 작성기가 자동 저장한 **내 초안**이다(캠페인 × 나라 × 작성자). 발송 러너가 그 초안 id 로
 * 명단을 읽으므로 미리보기와 발송이 같은 것을 본다(게이트 §3 불변 7).
 */
export function SendConfirmContainer({
  scenarioId,
  country,
  onGoTo,
  onSent,
}: SendConfirmContainerProps) {
  const user = useAuthStore((state) => state.user);
  const scenario = useScenarioDetail(scenarioId);
  const draft = useOutreachDraft(scenarioId, country);
  const stibeeLists = useStibeeLists(scenarioId);
  // 발송 확인도 이 나라 초안을 만지는 자리다(IA §3-7).
  useOutreachPresenceLease(scenarioId, country, true);
  const draftId = draft.data?.id ?? null;

  // 주소 → 푼 묶음. 서버 `planRecipients` 는 같은 주소의 두 번째 이후 계정만 이 목록을 보므로
  // 묶음의 첫 계정(이미 한 통을 받는 쪽)을 뺀 나머지를 전부 싣는다.
  const [splitGroups, setSplitGroups] = useState<ReadonlyMap<string, SharedAddressGroup>>(
    new Map()
  );
  // 쿼리 키에 들어가므로 순서를 고정한다 — 같은 집합이 다른 키로 두 번 불리지 않게.
  const perCreatorSendIds = useMemo(
    () =>
      [...splitGroups.values()]
        .flatMap((group) => group.members.slice(1).map((member) => member.externalCreatorId))
        .sort(),
    [splitGroups]
  );

  /*
    이미 답한 사람 중 이번 발송에 다시 넣을 사람(12bcfe19). 초안에 저장하지 않는다 — 저장하면 다음
    묶음·다른 초안까지 따라간다(재포함 명세 §2-2). 따로 보내기와 같은 자리의 화면 상태이고, 미리보기와
    발송·예약 요청이 같은 목록을 받는다.
  */
  const [reincludeSet, setReincludeSet] = useState<ReadonlySet<string>>(new Set());
  const reincludeIds = useMemo(() => [...reincludeSet].sort(), [reincludeSet]);

  const preview = useOutreachBulkPreview(scenarioId, draftId, perCreatorSendIds, reincludeIds);
  const outliers = useOutreachOutlierSummary(scenarioId, draftId);
  const send = useSendOutreachBulk(scenarioId);
  const reserve = useReserveOutreachBulk(scenarioId);
  // 예약 시간대 기본값은 초안 발송 나라의 것이다(IA §3-5, 2026-09-18).
  const defaultZone = defaultOutreachTimeZone(country);
  const [mode, setMode] = useState<SendMode>('now');
  const [schedule, setSchedule] = useState<ScheduleDraft>({ local: '', timeZone: defaultZone });
  const timeZoneOptions = useMemo(() => timeZoneOptionsFor(defaultZone), [defaultZone]);
  const testSend = useSendOutreachTestMail(scenarioId, country);

  if (draft.isPending) return <p className={plainNote}>메일 초안을 불러오는 중입니다.</p>;
  if (draft.isError) {
    return (
      <p className={plainNote}>
        {getApiErrorMessage(draft.error, '메일 초안을 불러오지 못했습니다.')}
      </p>
    );
  }
  const draftRow = draft.data;
  if (!draftRow) {
    return (
      <div>
        <p className={plainNote}>아직 저장된 메일 초안이 없습니다. 메일을 먼저 써 주세요.</p>
        <Button variant="secondary" size="sm" onClick={() => onGoTo('composer')}>
          메일 작성으로 돌아가기
        </Button>
      </div>
    );
  }
  if (preview.isPending) return <p className={plainNote}>보낼 명단을 확인하는 중입니다.</p>;
  if (preview.isError) {
    return (
      <p className={plainNote}>
        {getApiErrorMessage(preview.error, '보낼 명단을 확인하지 못했습니다.')}
      </p>
    );
  }

  const content = draftRow.content;
  /*
    주소록과 보내는 사람은 **초안이 든다**(fact #2956). 스티비는 발신자를 주소록마다 인증해서,
    로그인한 사람의 업무 주소로 대신 채우면 등록만 되고 발송이 조용히 멈춘다(2026-09-28 실측
    1회차). 비어 있으면 막고 작성기로 돌려보낸다 — 서버도 같은 판정으로 막는다.
  */
  const list = stibeeLists.data?.items.find((row) => row.listId === content.listId) ?? null;
  const sender = {
    listName: list?.displayName ?? null,
    name: user?.username ?? '',
    email: content.senderEmail ?? '',
  };
  // 테스트는 누른 사람의 Gmail 연결로 나가고 그 사람이 받는다 — 보내는 사람(회사 공용 주소)과 다르다.
  const testRecipient = user?.email ?? '';
  const subject = content.subject ?? '';
  const campaignProductUrl = scenario.data?.productUrl ?? null;
  const formUrl = outreachFormRespondUrl(scenario.data?.outreachFormUrl ?? '');
  const answeredCount = outliers.isSuccess
    ? (outliers.data.groups.find((group) => group.code === 'ALREADY_ANSWERED')?.count ?? 0)
    : null;

  /*
    재포함의 근거 — 미리보기가 사람별로 돌려준 **마지막 회신·폼 제출 시각**. 발송·예약 요청이 이것을
    들고 가고, 서버가 지금 읽은 시각과 비교해 더 늦은 사건이 있으면 요청 전체를 거절한다. 거절되면
    미리보기를 다시 불러 바뀐 근거를 보이고 담당자가 다시 확인한다(재포함 명세 §2-4).
  */
  const reincludeBody =
    reincludeIds.length === 0
      ? {}
      : {
          reincludeIds,
          reincludeEvidenceAt: Object.fromEntries(
            preview.data.reincluded.flatMap((entry) =>
              entry.evidenceAt === null ? [] : [[entry.externalCreatorId, entry.evidenceAt]]
            )
          ),
        };
  const onMutationError = (error: unknown) => {
    if (getApiErrorMeta(error)?.errorCode === REINCLUDE_CHANGED) void preview.refetch();
  };

  // 서버(`bulk-send.runner`)와 같은 판정이다 — 받는 사람마다 같은 값이라 못 채우면 전체를 막는다.
  const unfilled = unfilledOutreachVariables(
    content,
    outreachProductName(scenario.data?.products ?? [])
  );

  const draftBlockedReason = preview.isPlaceholderData
    ? '따로 보내기를 반영해 보낼 명단을 다시 세는 중입니다.'
    : list === null || sender.email === ''
      ? NO_SENDER_REASON
      : formUrl === null
        ? '캠페인에 설문폼 링크가 없어 보낼 수 없습니다.'
        : subject.trim() === ''
          ? '제목이 비어 있어 보낼 수 없습니다.'
          : unfilled.includes('제품명')
            ? '캠페인 제품이 하나가 아니라 본문의 {제품명} 자리를 채울 수 없어 보낼 수 없습니다. 캠페인 제품을 하나로 맞추거나 본문에서 {제품명}을 지워 주세요.'
            : unfilled.includes('금액')
              ? '본문에 {금액} 자리가 있는데 이번 제안 금액이 비어 보낼 수 없습니다. 메일 작성에서 금액을 적어 주세요.'
              : null;
  const sendBlockedReason =
    draftBlockedReason ??
    (preview.data.suppressionStale
      ? '수신거부·반송 명단을 최근에 확인하지 못해 지금은 보낼 수 없습니다. 잠시 뒤 다시 확인해 주세요.'
      : null) ??
    (mode === 'later' && schedule.local === '' ? '예약 일시를 골라 주세요.' : null);
  const mutation = mode === 'later' ? reserve : send;

  return (
    <SendConfirmPanel
      preview={preview.data}
      sender={sender}
      testRecipient={testRecipient}
      subject={subject}
      hasBody={content.body.trim() !== ''}
      hasImage={Boolean(content.image)}
      // 작성기의 `productUrlDiffersFromCampaign` 과 같은 판정 — 다른 feature 라 import 하지 않는다.
      productUrlDiffers={
        campaignProductUrl !== null && (content.productUrl ?? null) !== campaignProductUrl
      }
      formUrl={formUrl}
      splitGroups={[...splitGroups.values()]}
      onToggleSplit={(group) =>
        setSplitGroups((current) => {
          const next = new Map(current);
          if (next.has(group.email)) next.delete(group.email);
          else next.set(group.email, group);
          return next;
        })
      }
      reincludeSlot={
        <SendConfirmReinclude
          scenarioId={scenarioId}
          draftId={draftRow.id}
          count={answeredCount}
          groupCode="ALREADY_ANSWERED"
          reincluded={preview.data.reincluded}
          selected={reincludeSet}
          onToggle={(id) =>
            setReincludeSet((current) => {
              const next = new Set(current);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            })
          }
        />
      }
      otherTeamReincludeSlot={
        <SendConfirmReinclude
          scenarioId={scenarioId}
          draftId={draftRow.id}
          count={otherTeamTotalOf(preview.data)}
          groupCode="OTHER_TEAM_RECENT"
          reincluded={preview.data.reincluded}
          selected={reincludeSet}
          onToggle={(id) =>
            setReincludeSet((current) => {
              const next = new Set(current);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            })
          }
        />
      }
      onGoTo={onGoTo}
      sending={mutation.isPending}
      sendBlockedReason={sendBlockedReason}
      sendError={
        mutation.isError
          ? getApiErrorMessage(
              mutation.error,
              mode === 'later' ? '예약하지 못했습니다.' : '발송하지 못했습니다.'
            )
          : null
      }
      mode={mode}
      onModeChange={setMode}
      schedule={schedule}
      onScheduleChange={setSchedule}
      timeZoneOptions={timeZoneOptions}
      onReserve={() => {
        if (sendBlockedReason !== null) return;
        reserve.mutate(
          {
            draftId: draftRow.id,
            perCreatorSendIds,
            senderName: sender.name,
            schedule,
            ...reincludeBody,
          },
          { onSuccess: onSent, onError: onMutationError }
        );
      }}
      onSend={() => {
        if (sendBlockedReason !== null) return;
        // 주소록·보내는 사람·제목·폼은 서버가 초안과 캠페인 폼에서 읽는다 — 요청에 싣지 않는다.
        send.mutate(
          { draftId: draftRow.id, perCreatorSendIds, senderName: sender.name, ...reincludeBody },
          { onSuccess: onSent, onError: onMutationError }
        );
      }}
      onSendTest={() => {
        if (testRecipient !== '') testSend.mutate({ to: testRecipient });
      }}
      testSendState={
        testSend.isPending
          ? 'sending'
          : testSend.isError
            ? 'error'
            : testSend.isSuccess
              ? 'sent'
              : 'idle'
      }
    />
  );
}

/** 서버의 남은 인원에 다시 넣은 사람을 더한 다른 팀 연락 전체 인원. 확인하지 못했으면 `null`. */
function otherTeamTotalOf(preview: {
  otherTeamRecentCount?: number | null;
  reincluded: readonly { reason: string }[];
}): number | null {
  if (preview.otherTeamRecentCount == null) return null;
  return (
    preview.otherTeamRecentCount +
    preview.reincluded.filter((entry) => entry.reason === 'OTHER_TEAM_RECENT').length
  );
}
